// src/features/localization/TranslationGrid.tsx
// Per-locale editing grid for the selected key — one row per available
// locale, missing-translation highlighting, plurals/placeholders visible.
// SetTranslation (upsert), ReviewTranslation, ApproveTranslation, GetTranslation.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert, Badge, Button } from '@react/components';
import { useLocalization } from '@react/hooks';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { InlineEditText } from '@/components/form';
import { localizationApi } from '@core/api';
import type { Locale, Translation, TranslationKey } from '@core/types/localization';
import { useScope } from '@/hooks/useScope';
import { errMessage, makeEntity, translationStatusBadgeVariant } from './helpers';

export interface TranslationGridProps {
  translationKey: TranslationKey;
  locales: Locale[];
  /** Key edited (source text, status, …) — parent should refresh its list. */
  onKeyChanged: (key: TranslationKey) => void;
  /** Key deleted — parent should clear the selection and refresh its list. */
  onKeyDeleted: () => void;
}

export function TranslationGrid({ translationKey, locales, onKeyChanged, onKeyDeleted }: TranslationGridProps) {
  const { t } = useI18n();
  const scope = useScope();
  const store = useLocalization();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [keyBusy, setKeyBusy] = useState(false);

  const [translations, setTranslations] = useState<Translation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyLocale, setBusyLocale] = useState<string | null>(null);

  const keyId = translationKey.entity?.id ?? '';

  const load = () => {
    setIsLoading(true);
    setError(null);
    localizationApi
      .ListTranslations()
      .then((res) => {
        const all = res.items?.items ?? [];
        setTranslations(all.filter((tr) => tr.key?.id === keyId));
      })
      .catch((e) => setError(errMessage(e, t('localization.translations.loadError'))))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
    setDrafts({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyId]);

  const byLocale = (code: string) => translations.find((tr) => tr.locale_code === code);

  const commit = async (localeCode: string) => {
    const draftValue = drafts[localeCode];
    if (draftValue === undefined) return;
    setBusyLocale(localeCode);
    setError(null);
    try {
      await store.SetTranslation({
        entity: makeEntity('', 'translation', scope),
        key: translationKey.entity!,
        locale_code: localeCode,
        value: draftValue,
      });
      load();
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[localeCode];
        return next;
      });
    } catch (e) {
      setError(errMessage(e, t('localization.translations.saveError')));
    } finally {
      setBusyLocale(null);
    }
  };

  const renameSourceText = async (sourceText: string) => {
    if (!translationKey.entity?.id) return;
    setKeyBusy(true);
    setError(null);
    try {
      const res = await store.UpdateTranslationKey(translationKey.entity.id, {
        translation_key: { ...translationKey, source_text: sourceText },
      });
      if (res.translation_key) onKeyChanged(res.translation_key);
    } catch (e) {
      setError(errMessage(e, t('localization.keys.saveError')));
    } finally {
      setKeyBusy(false);
    }
  };

  const toggleDeprecated = async () => {
    if (!translationKey.entity?.id) return;
    const nextStatus = translationKey.status === 'TRANSLATION_KEY_STATUS_DEPRECATED' ? 'TRANSLATION_KEY_STATUS_ACTIVE' : 'TRANSLATION_KEY_STATUS_DEPRECATED';
    setKeyBusy(true);
    setError(null);
    try {
      const res = await store.UpdateTranslationKey(translationKey.entity.id, {
        translation_key: { ...translationKey, status: nextStatus },
      });
      if (res.translation_key) onKeyChanged(res.translation_key);
    } catch (e) {
      setError(errMessage(e, t('localization.keys.saveError')));
    } finally {
      setKeyBusy(false);
    }
  };

  const deleteKey = async () => {
    if (!translationKey.entity?.id) return;
    setKeyBusy(true);
    setError(null);
    try {
      await store.DeleteTranslationKey(translationKey.entity.id);
      setDeleteConfirmOpen(false);
      onKeyDeleted();
    } catch (e) {
      setError(errMessage(e, t('localization.keys.saveError')));
      setDeleteConfirmOpen(false);
    } finally {
      setKeyBusy(false);
    }
  };

  const review = async (tr: Translation) => {
    if (!tr.entity?.id) return;
    setBusyLocale(tr.locale_code);
    try {
      await store.ReviewTranslation(tr.entity.id, { translation: tr.entity });
      load();
    } catch (e) {
      setError(errMessage(e, t('localization.translations.saveError')));
    } finally {
      setBusyLocale(null);
    }
  };

  const approve = async (tr: Translation) => {
    if (!tr.entity?.id) return;
    setBusyLocale(tr.locale_code);
    try {
      await store.ApproveTranslation(tr.entity.id, { translation: tr.entity });
      load();
    } catch (e) {
      setError(errMessage(e, t('localization.translations.saveError')));
    } finally {
      setBusyLocale(null);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium text-foreground">{translationKey.key}</span>
            {translationKey.is_plural && <Badge variant="secondary">{t('localization.keys.plural')}</Badge>}
          </div>
          <div className="flex shrink-0 gap-1.5">
            <Button size="sm" variant="ghost" isLoading={keyBusy} onClick={toggleDeprecated}>
              {translationKey.status === 'TRANSLATION_KEY_STATUS_DEPRECATED' ? t('localization.keyStatus.active') : t('localization.keyStatus.deprecated')}
            </Button>
            <Button size="sm" variant="danger" onClick={() => setDeleteConfirmOpen(true)}>{t('common.delete')}</Button>
          </div>
        </div>
        <InlineEditText
          value={translationKey.source_text}
          onCommit={renameSourceText}
          aria-label={t('localization.keys.sourceTextPlaceholder')}
          className="mt-1"
        />
        {translationKey.placeholders.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {translationKey.placeholders.map((p) => (
              <span key={p} className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">{`{${p}}`}</span>
            ))}
          </div>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {isLoading && <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy="true" />}

      {!isLoading && (
        <div className="space-y-2">
          {locales.map((loc) => {
            const code = loc.code;
            const tr = byLocale(code);
            const missing = !tr || !tr.value.trim();
            const draft = drafts[code] ?? tr?.value ?? '';
            return (
              <div key={code} className={`rounded-md border p-2.5 ${missing ? 'border-destructive/40 bg-destructive/5' : 'border-border'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-foreground">
                    {loc.flag_emoji} {loc.native_name || loc.name} <span className="text-muted-foreground">({code})</span>
                  </span>
                  <div className="flex items-center gap-1.5">
                    {missing && <Badge variant="warning">{t('localization.translations.missing')}</Badge>}
                    {!missing && tr?.status && (
                      <Badge variant={translationStatusBadgeVariant(tr.status)}>
                        {t(`localization.translationStatus.${tr.status.replace('TRANSLATION_STATUS_', '').toLowerCase()}`)}
                      </Badge>
                    )}
                  </div>
                </div>
                <textarea
                  value={draft}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [code]: e.target.value }))}
                  onBlur={() => commit(code)}
                  rows={1}
                  placeholder={t('localization.translations.valuePlaceholder')}
                  className="mt-1.5 w-full resize-none rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
                {tr && !missing && (
                  <div className="mt-1.5 flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" isLoading={busyLocale === code} onClick={() => review(tr)}>
                      {t('localization.translations.review')}
                    </Button>
                    <Button size="sm" variant="ghost" isLoading={busyLocale === code} onClick={() => approve(tr)}>
                      {t('localization.translations.approve')}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirmOpen}
        title={t('localization.keys.deleteTitle')}
        description={t('localization.keys.deleteDescription')}
        destructive
        isConfirming={keyBusy}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={deleteKey}
      />
    </div>
  );
}
